/**
 * Youidian Payment SDK - Server Module
 * 用于服务端集成，包含签名、订单创建、回调解密等功能
 */

import crypto from "crypto"

/**
 * Order status response
 */
export interface OrderStatus {
	orderId: string
	status: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "FAILED" | "EXPIRED"
	paidAt?: string
	channelTransactionId?: string
}

export type SubscriptionAction = "NEW" | "UPGRADE" | "DOWNGRADE"
export type SubscriptionIntent = "STANDARD" | "UPGRADE" | "QUEUED_UPGRADE"

export type SubscriptionStatus =
	| "PENDING"
	| "ACTIVE"
	| "EXPIRED"
	| "REPLACED"
	| "CANCELLED"

export type SubscriptionAutoRefundStatus = "PROCESSING" | "SUCCEEDED" | "FAILED"

export type SubscriptionErrorCode =
	| "SUBSCRIPTION_UPGRADE_NOT_AVAILABLE"
	| "SUBSCRIPTION_SAME_LEVEL_NOT_ALLOWED"
	| "SUBSCRIPTION_DOWNGRADE_NOT_ALLOWED"
	| "SUBSCRIPTION_PENDING_ORDER_EXISTS"
	| "SUBSCRIPTION_NO_REMAINING_DAYS"
	| "SUBSCRIPTION_CURRENCY_MISMATCH"
	| "SUBSCRIPTION_PERIOD_MISMATCH"
	| "SUBSCRIPTION_PRICING_MANAGED"

export interface SubscriptionBillingSnapshot {
	version: number
	action: SubscriptionAction
	isUpgrade: boolean
	quoteBusinessDate: string
	timezone: string
	periodDays: number
	subscriptionGroup: string
	subscriptionLevel: number
	productCode: string
	standardAmount: number
	policyDiscountAmount: number
	policyId: string | null
	policyCode: string | null
	policyName: string | null
	policyRevision: number | null
	qualificationCode: string | null
	intent?: SubscriptionIntent | null
	fromSubscriptionId?: string | null
	managedSubscription?: boolean
	fromProductCode?: string | null
	remainingDays?: number | null
	currentFullPlanValueAmount?: number | null
	currentRemainingValueAmount?: number | null
	targetFullPlanValueAmount?: number | null
	targetRemainingValueAmount?: number | null
	appliedDiscountAmount?: number | null
	finalAmount?: number | null
	startsAt?: string | null
	cycleStartsAt?: string | null
	expiresAt?: string | null
	previousSubscriptionExpiresAt?: string | null
	queueMode?: "INSERT_AFTER_ACTIVE" | null
	queueAdjustments?: SubscriptionQueueAdjustment[]
}

export interface SubscriptionQueueAdjustment {
	subscriptionId: string
	sourceOrderId: string
	startsAt: string
	cycleStartsAt: string
	expiresAt: string
	replacesSubscriptionId: string | null
}

export interface BillingSnapshot {
	version: number
	standardAmount: number
	discountAmount: number
	upgradeCreditAmount: number
	targetRemainingValueAmount: number | null
	fullPlanValueAmount: number | null
	discountPolicyCode: string | null
	discountPolicyRevision: number | null
	qualificationCode: string | null
	refundRequired: boolean
	autoRefundStatus: SubscriptionAutoRefundStatus | null
	subscription: SubscriptionBillingSnapshot | null
	customAmount: Record<string, unknown> | null
	merchantPricing: MerchantPricingSnapshot | null
}

export interface SubscriptionDetails {
	id: string
	action: SubscriptionAction
	status: SubscriptionStatus
	productId: string
	productCode: string
	priceId: string
	subscriptionGroup: string
	subscriptionLevel: number
	startsAt: string
	cycleStartsAt: string
	expiresAt: string
	periodDays: number
	timezone: string
	fullPlanValueAmount: number
	currency: string
	replacesSubscriptionId: string | null
	activatedAt: string | null
	replacedAt: string | null
	cancelledAt: string | null
}

export class PaymentApiError extends Error {
	readonly status: number
	readonly code?: string

	constructor(message: string, status: number, code?: string) {
		super(message)
		this.name = "PaymentApiError"
		this.status = status
		this.code = code
	}
}

/**
 * Order details response (full order information)
 */
export interface OrderDetails {
	orderId: string
	internalId: string
	merchantUserId: string
	productCode: string | null
	status: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "FAILED" | "EXPIRED"
	amount: number
	currency: string
	description?: string
	paidAt?: string
	createdAt: string
	channel?: string
	merchantPricing?: MerchantPricingSnapshot
	billing: BillingSnapshot
	subscription: SubscriptionDetails | null
	pricingBreakdown?: PricingBreakdown
	upgrade?: {
		isUpgrade: boolean
		fromProductId?: string | null
		fromProductCode?: string | null
		fromPriceId?: string | null
		fromOrderId?: string | null
		fromSourceKind?: string | null
		fromSortOrder?: number | null
		toSortOrder?: number | null
		originalAmount?: number | null
		creditAmount?: number | null
		finalPayableAmount?: number | null
		remainingRatio?: number | null
		newPeriodStartsAt?: string | null
		newPeriodValue?: number | null
		newPeriodUnit?: "days" | "months" | "years" | null
	} | null
	product?: {
		code: string
		type: string
		name: string
		description?: string
		entitlements: ProductEntitlements
		metadata?: ProductMetadata | null
	}
}

/**
 * Product Entitlements
 */
export interface ProductEntitlements {
	[key: string]: any
}

/**
 * Product Price
 */
export interface ProductPrice {
	id: string
	currency: string
	amount: number
	displayAmount: string
	locale: string | null
	isDefault: boolean
}

export interface ProductSubscriptionPeriod {
	value: number
	unit: "days" | "months" | "years"
}

export interface ProductResetRule {
	resetInterval: "day" | "month"
}

export interface ProductCustomAmountCurrency {
	minAmount: number
	maxAmount: number
	stepAmount?: number
	unitsPerCurrencyUnit: number
	unitsPerCurrencyUnitBasis?: "MINOR" | "MAJOR"
}

export interface ProductCustomAmount {
	enabled: boolean
	entitlementKey: string
	currencies: Record<string, ProductCustomAmountCurrency>
}

export interface ProductInventory {
	enabled: boolean
	totalQuantity: number
	reserveTimeoutSeconds?: number
}

export interface ProductMetadata {
	subscriptionPeriod?: ProductSubscriptionPeriod
	subscriptionGroup?: string
	restrictSubscriptionPurchase?: boolean
	expiringEntitlements?: string[]
	resetEntitlements?: Record<string, ProductResetRule>
	autoAssignOnNewUser?: boolean
	trialDurationDays?: number
	customAmount?: ProductCustomAmount
	inventory?: ProductInventory
}

export type ProductStockLookupMode = "auto" | "id" | "code"

export interface ProductStock {
	productId: string
	productCode: string
	limited: boolean
	total: number | null
	reserved: number
	sold: number
	available: number | null
	updatedAt: string
	reserveTimeoutSeconds: number | null
}

export interface ProductStockQueryOptions {
	lookupBy?: ProductStockLookupMode
	locale?: string
	currency?: string
}

export interface ProductStocksQueryParams {
	productIds?: string[]
	productCodes?: string[]
}

export interface PricingBreakdown {
	isUpgrade: boolean
	originalAmount: number
	creditAmount: number
	finalPayableAmount: number
	fromProductCode?: string | null
}

export interface ConsumeEntitlementPoolResult {
	balance: number
	balances: Record<string, number>
	consumed: Record<string, number>
	consumedBuckets?: EntitlementCreditBucketConsumption[]
}

export interface EntitlementCreditBucket {
	key: string
	grantId: string | null
	sourceKind: string
	sourceProductId: string | null
	sourceOrderId: string | null
	amount: number
	remaining: number
	expiresAt: string | null
	grantedAt: string | null
	metadata?: Record<string, any> | null
}

export interface EntitlementCreditBucketConsumption {
	key: string
	grantId: string | null
	amount: number
	expiresAt: string | null
	sourceProductId: string | null
	sourceOrderId: string | null
}

export interface ActiveSubscriptionInfo {
	subscriptionId?: string
	productId: string
	productCode: string
	sortOrder: number
	subscriptionLevel?: number
	subscriptionGroup?: string | null
	paidAt?: string | null
	cycleStartsAt?: string
	expiresAt: string
	periodDays?: number
	timezone?: string
	priceId?: string | null
	orderId?: string | null
	sourceKind?: string | null
	currency?: string | null
}

export interface DiscountPolicy {
	code: string
	name: string
	description: string | null
	qualificationCode: string
	balancePackThresholdAmount: number | null
	productId: string
	productCode: string
	priceId: string
	currency: string
	standardAmount: number
	discountAmount: number
	startsAt: string | null
	endsAt: string | null
}

export interface UserProductDiscountEligibility {
	userId: string
	productId: string
	productCode: string
	eligible: boolean
	coupons: Array<{
		coupon: DiscountPolicy
		eligible: boolean
		grantedAt: string | null
	}>
}

/**
 * Product Data
 */
export interface Product {
	id: string
	code: string
	type: string
	name: string
	description?: string
	entitlements: ProductEntitlements
	prices: ProductPrice[]
	subscriptionGroup?: string | null
	subscriptionLevel?: number | null
	subscriptionPeriodDays?: number | null
	subscriptionTimezone?: string | null
	metadata?: ProductMetadata | null
	discountEligibility?: ProductDiscountEligibility
}

export interface ProductDiscountEligibility {
	eligible: boolean
	coupons: Array<{
		code: string
		name: string
		currency: string
		standardAmount: number
		discountAmount: number
		discountedAmount: number
		grantedAt: string | null
	}>
}

export interface CustomAmountRechargeRule extends ProductCustomAmountCurrency {
	productId: string
	productCode: string
	entitlementKey: string
	currency: string
	configuredMinAmount: number
	minimumGrantAmount: number
}

export type CustomAmountRechargeValidationResult =
	| {
			valid: true
			rule: CustomAmountRechargeRule
	  }
	| {
			valid: false
			code:
				| "CUSTOM_AMOUNT_UNAVAILABLE"
				| "CUSTOM_AMOUNT_INVALID_AMOUNT"
				| "CUSTOM_AMOUNT_OUT_OF_RANGE"
				| "CUSTOM_AMOUNT_INVALID_STEP"
			error: string
			rule?: CustomAmountRechargeRule
	  }

/**
 * Resolve the custom amount recharge rule for a product and currency.
 *
 * Amounts are in the smallest currency unit. `unitsPerCurrencyUnit` defaults
 * to the smallest currency unit. When `unitsPerCurrencyUnitBasis` is `MAJOR`,
 * fractional entitlement amounts are rounded to the nearest whole unit. The
 * returned `minAmount` is the effective lower bound that both satisfies product
 * metadata and grants at least one entitlement unit. `configuredMinAmount`
 * keeps the raw product metadata value for display or diagnostics.
 */
export function getCustomAmountRechargeRule(
	product: Product | null | undefined,
	currency: string,
): CustomAmountRechargeRule | null {
	if (!product || product.type !== "CREDIT") return null
	const normalizedCurrency = currency.trim().toUpperCase()
	if (!/^[A-Z]{3}$/.test(normalizedCurrency)) return null

	const customAmount = product.metadata?.customAmount
	if (customAmount?.enabled !== true) return null

	const currencyConfig = customAmount.currencies?.[normalizedCurrency]
	if (!currencyConfig) return null

	const minimumGrantAmount =
		currencyConfig.unitsPerCurrencyUnitBasis === "MAJOR"
			? Math.ceil(50 / currencyConfig.unitsPerCurrencyUnit)
			: Math.ceil(1 / currencyConfig.unitsPerCurrencyUnit)
	let minAmount = Math.max(currencyConfig.minAmount, minimumGrantAmount)
	if (currencyConfig.stepAmount && minAmount > currencyConfig.minAmount) {
		const stepCount = Math.ceil(
			(minAmount - currencyConfig.minAmount) / currencyConfig.stepAmount,
		)
		minAmount = currencyConfig.minAmount + stepCount * currencyConfig.stepAmount
	}

	return {
		...currencyConfig,
		productId: product.id,
		productCode: product.code,
		entitlementKey: customAmount.entitlementKey,
		currency: normalizedCurrency,
		configuredMinAmount: currencyConfig.minAmount,
		minimumGrantAmount,
		minAmount,
	}
}

/**
 * Validate a custom recharge amount before calling `PaymentUI.openPayment`.
 * This is an SDK-side guardrail for integrator-owned amount inputs; the worker
 * still performs authoritative server-side validation when creating the order.
 */
export function validateCustomAmountRecharge(
	product: Product | null | undefined,
	customAmount: { amount: number; currency: string },
): CustomAmountRechargeValidationResult {
	const rule = getCustomAmountRechargeRule(product, customAmount.currency)
	if (!rule) {
		return {
			valid: false,
			code: "CUSTOM_AMOUNT_UNAVAILABLE",
			error:
				"Product does not support custom amount recharge for this currency.",
		}
	}

	const amount = Number(customAmount.amount)
	if (!Number.isInteger(amount) || amount <= 0) {
		return {
			valid: false,
			code: "CUSTOM_AMOUNT_INVALID_AMOUNT",
			error:
				"Custom amount must be a positive integer in the smallest currency unit.",
			rule,
		}
	}

	if (amount < rule.minAmount || amount > rule.maxAmount) {
		return {
			valid: false,
			code: "CUSTOM_AMOUNT_OUT_OF_RANGE",
			error: `Custom amount must be between ${rule.minAmount} and ${rule.maxAmount}.`,
			rule,
		}
	}

	if (
		rule.stepAmount &&
		(amount - rule.configuredMinAmount) % rule.stepAmount !== 0
	) {
		return {
			valid: false,
			code: "CUSTOM_AMOUNT_INVALID_STEP",
			error: `Custom amount must follow step ${rule.stepAmount}.`,
			rule,
		}
	}

	return { valid: true, rule }
}

/**
 * WeChat JSAPI Payment Parameters (for wx.requestPayment)
 */
export interface WechatJsapiPayParams {
	appId: string
	timeStamp: string
	nonceStr: string
	package: string
	signType: "RSA"
	paySign: string
}

/**
 * Verified hosted login token payload.
 */
export interface VerifiedLoginToken {
	appId: string
	userId: string
	legacyCasdoorId?: string | null
	channel: string
	email?: string | null
	emailVerified?: boolean | null
	name?: string | null
	username?: string | null
	avatar?: string | null
	phoneCountryCode?: string | null
	phoneNumber?: string | null
	phoneE164?: string | null
	phoneVerifiedAt?: string | null
	wechatOpenId?: string | null
	wechatUnionId?: string | null
	expiresAt: string
}

export interface SendPhoneVerificationCodeParams {
	userId: string
	phoneCountryCode?: string
	countryCode?: string
	phoneNumber: string
}

export interface SendPhoneVerificationCodeResponse {
	phoneCountryCode?: string | null
	phoneNumber?: string | null
	phoneE164: string
	expiresAt: string
	cooldownSeconds?: number
	resendAfterSeconds?: number
}

export interface PhoneBinding {
	userId: string
	phoneCountryCode?: string | null
	phoneNumber?: string | null
	phoneE164?: string | null
	phoneVerifiedAt?: string | null
	bound: boolean
}

export interface GetPhoneBindingParams {
	userId: string
}

export interface BindPhoneNumberParams {
	userId: string
	phoneCountryCode?: string
	countryCode?: string
	phoneNumber: string
	code: string
}

export type UpdatePhoneNumberParams = BindPhoneNumberParams

export interface BindPhoneNumberResponse {
	user: {
		userId: string
		phoneCountryCode?: string | null
		phoneNumber?: string | null
		phoneE164?: string | null
		phoneVerifiedAt?: string | null
	}
	merged: boolean
	mergeSummary?: Record<string, any>
}

export type UpdatePhoneNumberResponse = BindPhoneNumberResponse

export interface CreateWechatMessageBindingParams {
	/** Hosted login external user ID returned by Youidian login. */
	userId?: string
	loginExternalUserId?: string
	/** Integrator-owned user ID when hosted login is not used. */
	merchantUserId?: string
	/** Optional linked template ID or code to infer the WeChat channel. */
	templateId?: string
	templateCode?: string
	/** Optional direct channel ID when no template is selected yet. */
	channelId?: string
	/** Optional locale for the hosted binding page. */
	locale?: string
	/** Optional URL to return to after binding succeeds. */
	returnUrl?: string
}

export interface CreateWechatMessageBindingResponse {
	ticketId: string
	status: "PENDING" | "BOUND" | "EXPIRED" | "FAILED"
	bindingUrl: string
	qrCodeUrl?: string | null
	expiresAt: string
}

export type MessageTemplateDataValue =
	| string
	| number
	| boolean
	| {
			value: string | number | boolean
			color?: string
	  }

export interface SendMessageParams {
	templateId?: string
	templateCode?: string
	userId?: string
	loginExternalUserId?: string
	merchantUserId?: string
	/** Direct SMS recipient phone number. When provided for SMS templates, binding is not required before sending. */
	phoneNumber?: string
	/** Country/region code for phoneNumber. Defaults to +86 on the gateway. */
	phoneCountryCode?: string
	/** Alias for phoneCountryCode. */
	countryCode?: string
	/** Direct E.164 SMS recipient. Alternative to phoneNumber. */
	phoneE164?: string
	data: Record<string, MessageTemplateDataValue> | MessageTemplateDataValue[]
	url?: string
	redirectUrl?: string
	miniProgram?: {
		appid: string
		pagepath?: string
	}
	idempotencyKey?: string
}

export interface SendMessageResponse {
	logId: string
	status: "PENDING" | "SENT" | "FAILED" | "SKIPPED"
	providerMessageId?: string | null
	errorCode?: string | null
	errorMessage?: string | null
	bindRequired?: boolean
	idempotent?: boolean
	phoneBinding?: {
		bound: boolean
		userId?: string
		phoneE164?: string | null
		merged?: boolean
		alreadyBound?: boolean
		error?: string
	} | null
}

/**
 * SDK Client Options
 */
export interface PaymentClientOptions {
	/** Application ID (Required) */
	appId: string
	/** Application Secret (Required for server-side operations) */
	appSecret: string

	/**
	 * @deprecated Use apiUrl and checkoutUrl instead
	 * API Base URL (e.g. https://pay.youidian.com)
	 * If apiUrl or checkoutUrl is not provided, this will be used as fallback
	 * Default: https://pay.imgto.link
	 */
	baseUrl?: string

	/**
	 * API server URL for backend requests (e.g. https://api.youidian.com)
	 * Default: https://pay-api.imgto.link
	 */
	apiUrl?: string

	/**
	 * Checkout page URL for client-side payment (e.g. https://pay.youidian.com)
	 * Default: https://pay.imgto.link
	 */
	checkoutUrl?: string
}

export interface MerchantPricingBreakdownItem {
	type: "coupon" | "promotion" | "membership" | "manual" | "other"
	amount: number
	label?: string
	code?: string
}

export interface MerchantPricing {
	amount: number
	currency: string
	originalAmount?: number
	discountAmount?: number
	discountReason?: string
	discountCode?: string
	breakdown?: MerchantPricingBreakdownItem[]
}

export interface MerchantPricingSnapshot extends MerchantPricing {
	originalAmount: number
	discountAmount: number
	priceId: string
	productId: string
	productCode: string
}

/**
 * Create Order Parameters
 */
export interface CreateOrderParams {
	productId?: string
	priceId?: string
	channel?: string
	userId: string
	returnUrl?: string
	/** Public server endpoint used for both payment and refund webhooks. */
	callbackUrl?: string
	metadata?: Record<string, any>
	merchantOrderId?: string
	openid?: string
	locale?: string
	subscriptionIntent?: SubscriptionIntent
	customAmount?: {
		amount: number
		currency: string
	}
	merchantPricing?: MerchantPricing
}

export type CreateBankTransferOrderParams = Omit<CreateOrderParams, "channel">

/**
 * Create WeChat Mini Program Order Parameters
 */
export type CreateMiniProgramOrderParams = {
	userId: string
	openid: string
	merchantOrderId?: string
	priceId: string
	subscriptionIntent?: SubscriptionIntent
}

/**
 * Create Order Response
 */
export interface CreateOrderResponse {
	orderId: string
	internalId: string
	amount: number
	currency: string
	payParams: any
	pricingBreakdown?: PricingBreakdown
	status?: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "FAILED" | "EXPIRED"
	paidAt?: string | null
	channel?: string
	isSandbox?: boolean
	merchantPricing?: MerchantPricingSnapshot
	billing?: BillingSnapshot
	subscription?: SubscriptionDetails | null
}

export interface CancelOrderResponse {
	cancelled: boolean
	status: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "FAILED" | "EXPIRED"
	orderId: string
	internalId?: string | null
}

export interface RefundOrderParams {
	/** Refund amount in the currency's smallest unit. Omit for the full remainder. */
	amount?: number
	/** A unique key for this refund attempt. */
	idempotencyKey: string
	/** Send an ORDER_REFUNDED webhook after the refund is recorded. */
	notifyIntegrator?: boolean
	/** Revoke entitlements granted by this order after a full refund. */
	revokeEntitlements?: boolean
}

export interface RefundOrderResponse {
	orderId: string
	internalId: string
	status: "PAID" | "REFUNDED" | "PROCESSING"
	refundAmount: number
	refundId: string
	notifyIntegrator: boolean
	revokeEntitlements: boolean
	idempotentReplay: boolean
}

export interface CompleteFreeOrderResponse {
	completed: boolean
	status: "paid" | "pending" | "cancelled" | "refunded" | "failed" | "unknown"
	orderId: string
	internalId?: string | null
}

/**
 * Payment Callback Notification
 */
export interface PaymentNotification {
	iv: string
	encryptedData: string
	authTag: string
}

/**
 * Decrypted Payment Callback Data
 */
export interface PaymentCallbackData {
	orderId: string
	merchantOrderId?: string
	status: "PAID" | "CANCELLED" | "REFUNDED" | "FAILED"
	amount: number
	currency: string
	paidAt: string
	channelTransactionId?: string
	metadata?: Record<string, any>
}

/**
 * Platform webhook payload.
 *
 * The platform delivers this object as plaintext JSON in the webhook request
 * body, signed with `X-UniPay-Signature` / `X-UniPay-Timestamp` headers.
 * Validate with {@link PaymentClient.verifyWebhookSignature} using the raw
 * body string before parsing.
 */
export interface WebhookPayload {
	event: "ORDER_PAID" | "ORDER_CANCELLED" | "ORDER_REFUNDED" | "ORDER_FAILED"
	orderId: string
	merchantOrderId: string
	merchantUserId: string
	productCode: string
	billing: BillingSnapshot
	subscription: SubscriptionDetails | null
	amount: number
	currency: string
	paidAt: string | null
	channelTransactionId?: string
	cancelSubscription?: boolean
	revokeEntitlements?: boolean
}

/**
 * Get Orders Parameters
 */
export interface GetOrdersParams {
	page?: number
	pageSize?: number
	userId?: string
	status?: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "FAILED" | "EXPIRED"
	startDate?: string
	endDate?: string
}

/**
 * Order List Item
 */
export interface OrderListItem {
	orderId: string
	internalId: string
	merchantUserId: string
	status: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "FAILED" | "EXPIRED"
	amount: number
	currency: string
	channel?: string
	paidAt?: string
	createdAt: string
}

/**
 * Get Orders Response
 */
export interface GetOrdersResponse {
	orders: OrderListItem[]
	pagination: {
		total: number
		page: number
		pageSize: number
		totalPages: number
	}
}

/**
 * Entitlement Detail Item
 */
export interface EntitlementDetailItem {
	type: string
	current: number | boolean
	limit?: number
	expiresAt?: string | null
	resetInterval?: string | null
	nextResetAt?: string | null
	sourceKind?: string | null
}

/**
 * Entitlement Detail - returned by getEntitlementsDetail
 */
export type EntitlementDetail = Record<string, EntitlementDetailItem>

/**
 * Ensure User With Trial Response
 */
export interface EnsureUserWithTrialResponse {
	isNew: boolean
	trialAssigned: boolean
	trialProductCode?: string
	entitlements: EntitlementDetail
}

/**
 * Server-side Payment Client
 * 服务端支付客户端，用于创建订单、查询状态、解密回调
 */
export class PaymentClient {
	private readonly appId: string
	private readonly appSecret: string
	private readonly apiUrl: string // 用于 API 调用
	private readonly checkoutUrl: string // 用于生成 checkout URL

	constructor(options: PaymentClientOptions) {
		if (!options.appId) throw new Error("appId is required")
		if (!options.appSecret) throw new Error("appSecret is required")

		this.appId = options.appId
		this.appSecret = options.appSecret

		// apiUrl: 优先使用 apiUrl，其次 baseUrl，默认 https://pay-api.imgto.link
		const apiUrl =
			options.apiUrl || options.baseUrl || "https://pay-api.imgto.link"
		this.apiUrl = apiUrl.replace(/\/$/, "") // Remove trailing slash

		// checkoutUrl: 优先使用 checkoutUrl，其次 baseUrl，默认 https://pay.imgto.link
		const checkoutUrl =
			options.checkoutUrl || options.baseUrl || "https://pay.imgto.link"
		this.checkoutUrl = checkoutUrl.replace(/\/$/, "") // Remove trailing slash
	}

	/**
	 * Generate SHA256 signature for the request
	 * Logic: SHA256(appId + appSecret + timestamp)
	 */
	private generateSignature(timestamp: number): string {
		const str = `${this.appId}${this.appSecret}${timestamp}`
		return crypto.createHash("sha256").update(str).digest("hex")
	}

	/**
	 * Internal request helper for Gateway API
	 */
	private async request<T>(
		method: string,
		path: string,
		body?: any,
	): Promise<T> {
		const timestamp = Date.now()
		const signature = this.generateSignature(timestamp)
		const serializedBody = body ? JSON.stringify(body) : ""
		const contentSignature = crypto
			.createHmac("sha256", this.appSecret)
			.update(`${timestamp}.${method.toUpperCase()}.${path}.${serializedBody}`)
			.digest("hex")

		const url = `${this.apiUrl}/api/v1/gateway/${this.appId}${path}`

		const headers: HeadersInit = {
			"Content-Type": "application/json",
			"X-Pay-Timestamp": timestamp.toString(),
			"X-Pay-Sign": signature,
			"X-Pay-Sign-V2": contentSignature,
		}

		const options: RequestInit = {
			method,
			headers,
			body: body ? serializedBody : undefined,
		}

		const response = await fetch(url, options)

		if (!response.ok) {
			const errorText = await response.text()
			let parsedError: any = null
			try {
				parsedError = JSON.parse(errorText)
			} catch {}
			const message =
				parsedError?.message ||
				parsedError?.error ||
				errorText ||
				"Request failed"
			throw new PaymentApiError(
				message,
				response.status,
				parsedError?.code || undefined,
			)
		}

		const json = await response.json()
		if (json.error) {
			throw new PaymentApiError(
				`Payment API Error: ${json.error}`,
				response.status,
				json.code,
			)
		}

		return json.data as T
	}

	/**
	 * Decrypts the callback notification payload using AES-256-GCM.
	 *
	 * @deprecated This legacy encrypted-envelope protocol is NOT what the platform
	 * actually delivers. The platform sends an HMAC-signed plaintext JSON webhook
	 * with `X-UniPay-Signature` / `X-UniPay-Timestamp` headers; use
	 * {@link PaymentClient.verifyWebhookSignature} to validate those instead.
	 *
	 * @param notification - The encrypted notification from payment webhook
	 * @returns Decrypted payment callback data
	 */
	decryptCallback(notification: PaymentNotification): PaymentCallbackData {
		try {
			const { iv, encryptedData, authTag } = notification
			const key = crypto.createHash("sha256").update(this.appSecret).digest()
			const decipher = crypto.createDecipheriv(
				"aes-256-gcm",
				key,
				Buffer.from(iv, "hex"),
			)

			decipher.setAuthTag(Buffer.from(authTag, "hex"))

			let decrypted = decipher.update(encryptedData, "hex", "utf8")
			decrypted += decipher.final("utf8")

			return JSON.parse(decrypted)
		} catch {
			throw new Error(
				"Failed to decrypt payment callback: Invalid secret or tampered data.",
			)
		}
	}

	/**
	 * Verify an inbound platform webhook signature.
	 *
	 * The platform delivers webhooks as plaintext JSON with two headers:
	 * `X-UniPay-Signature` (HMAC-SHA256 of `${timestamp}.${rawBodyString}`) and
	 * `X-UniPay-Timestamp`. The body itself is NOT encrypted; verify the signature
	 * then `JSON.parse` the body to obtain a {@link WebhookPayload}.
	 *
	 * @param rawBody - The raw request body string (exactly as received)
	 * @param signature - The `X-UniPay-Signature` header value (hex)
	 * @param timestamp - The `X-UniPay-Timestamp` header value (ms epoch string)
	 * @param options - Optional `toleranceMs` for timestamp freshness (default 5 min)
	 * @returns `true` if the signature is valid and the timestamp is fresh
	 */
	verifyWebhookSignature(
		rawBody: string,
		signature: string,
		timestamp: string,
		options?: { toleranceMs?: number },
	): boolean {
		try {
			const toleranceMs = options?.toleranceMs ?? 5 * 60 * 1000
			const timestampMs = Number.parseInt(timestamp, 10)
			if (
				Number.isNaN(timestampMs) ||
				Math.abs(Date.now() - timestampMs) > toleranceMs
			) {
				return false
			}

			const rawString = `${timestamp}.${rawBody}`
			const expected = crypto
				.createHmac("sha256", this.appSecret)
				.update(rawString)
				.digest("hex")

			const signatureBuffer = Buffer.from(signature)
			const expectedBuffer = Buffer.from(expected)
			if (signatureBuffer.length !== expectedBuffer.length) {
				return false
			}
			return crypto.timingSafeEqual(signatureBuffer, expectedBuffer)
		} catch {
			return false
		}
	}

	/**
	 * Fetch products for the configured app.
	 */
	async getProducts(options?: {
		locale?: string
		currency?: string
		userId?: string
	}): Promise<Product[]> {
		const params = new URLSearchParams()
		if (options?.locale) params.append("locale", options.locale)
		if (options?.currency) params.append("currency", options.currency)
		if (options?.userId !== undefined) {
			const userId = options.userId.trim()
			if (!userId) throw new Error("userId must be a non-empty string")
			params.append("userId", userId)
		}

		const path = params.toString()
			? `/products?${params.toString()}`
			: "/products"
		return this.request("GET", path)
	}

	/** Fetch all currently active discount policies for the configured app. */
	async getDiscountPolicies(): Promise<DiscountPolicy[]> {
		return this.request("GET", "/discount-policies")
	}

	/** Fetch currently active discount policies for one product. */
	async getProductDiscountPolicies(
		productIdOrCode: string,
	): Promise<DiscountPolicy[]> {
		const value = productIdOrCode?.trim()
		if (!value) throw new Error("productIdOrCode is required")
		return this.request(
			"GET",
			`/products/${encodeURIComponent(value)}/discount-policies`,
		)
	}

	/** Query one user's discount eligibility for one product. */
	async getUserProductDiscountEligibility(
		userId: string,
		productIdOrCode: string,
	): Promise<UserProductDiscountEligibility> {
		const userValue = userId?.trim()
		const productValue = productIdOrCode?.trim()
		if (!userValue) throw new Error("userId is required")
		if (!productValue) throw new Error("productIdOrCode is required")
		return this.request(
			"GET",
			`/users/${encodeURIComponent(userValue)}/products/${encodeURIComponent(productValue)}/discount-eligibility`,
		)
	}

	/**
	 * Fetch the realtime stock snapshot for a single product.
	 */
	async getProductStock(
		productIdOrCode: string,
		options?: ProductStockQueryOptions,
	): Promise<ProductStock> {
		const value = productIdOrCode?.trim()
		if (!value) {
			throw new Error("productIdOrCode is required")
		}

		const params = new URLSearchParams()
		params.set("productIdOrCode", value)
		if (options?.lookupBy) params.set("lookupBy", options.lookupBy)
		if (options?.locale) params.set("locale", options.locale)
		if (options?.currency) params.set("currency", options.currency)

		return this.request(
			"GET",
			`/products/${encodeURIComponent(value)}/stock?${params.toString()}`,
		)
	}

	/**
	 * Fetch realtime stock snapshots for multiple products.
	 */
	async getProductStocks(
		params: ProductStocksQueryParams,
		options?: ProductStockQueryOptions,
	): Promise<ProductStock[]>
	async getProductStocks(
		params: ProductStocksQueryParams & ProductStockQueryOptions,
	): Promise<ProductStock[]>
	async getProductStocks(
		params: ProductStocksQueryParams & ProductStockQueryOptions,
		options?: ProductStockQueryOptions,
	): Promise<ProductStock[]> {
		const productIds = [
			...new Set(
				(params.productIds || []).map((item) => item.trim()).filter(Boolean),
			),
		]
		const productCodes = [
			...new Set(
				(params.productCodes || []).map((item) => item.trim()).filter(Boolean),
			),
		]
		if (productIds.length === 0 && productCodes.length === 0) {
			throw new Error("productIds or productCodes is required")
		}

		const queryOptions = options || params
		const query = new URLSearchParams()
		if (productIds.length > 0) query.set("productIds", productIds.join(","))
		if (productCodes.length > 0)
			query.set("productCodes", productCodes.join(","))
		if (queryOptions.lookupBy) query.set("lookupBy", queryOptions.lookupBy)
		if (queryOptions.locale) query.set("locale", queryOptions.locale)
		if (queryOptions.currency) query.set("currency", queryOptions.currency)

		return this.request("GET", `/products/stocks?${query.toString()}`)
	}

	/**
	 * Create a new order
	 * @param params - Order creation parameters
	 * @returns Order details with payment parameters
	 */
	async createOrder(params: CreateOrderParams): Promise<CreateOrderResponse> {
		return this.request("POST", "/orders", params)
	}

	/**
	 * Create a paid manual bank transfer order.
	 * @param params - Order creation parameters without channel
	 * @returns Paid order details
	 */
	async createBankTransferOrder(
		params: CreateBankTransferOrderParams,
	): Promise<CreateOrderResponse> {
		return this.request("POST", "/orders", {
			...params,
			channel: "BANK_TRANSFER",
		} satisfies CreateOrderParams)
	}

	/**
	 * Create a WeChat Mini Program order (channel fixed to WECHAT_MINI)
	 * @param params - Mini program order parameters
	 * @returns Order details with payment parameters
	 */
	async createMiniProgramOrder(
		params: CreateMiniProgramOrderParams,
	): Promise<CreateOrderResponse> {
		const { openid, ...rest } = params
		return this.request("POST", "/orders", {
			...rest,
			channel: "WECHAT_MINI",
			openid,
			metadata: { openid },
		} satisfies CreateOrderParams)
	}

	/**
	 * Pay for an existing order
	 * @param orderId - The order ID to pay
	 * @param params - Payment parameters including channel
	 */
	async payOrder(
		orderId: string,
		params: {
			channel: string
			returnUrl?: string
			openid?: string
			[key: string]: any
		},
	): Promise<CreateOrderResponse> {
		return this.request("POST", `/orders/${orderId}/pay`, params)
	}

	/**
	 * Cancel a pending order and release its inventory reservation.
	 * Paid/refunded/failed orders are returned unchanged by the gateway.
	 */
	async cancelOrder(
		orderId: string,
		params?: { reason?: string },
	): Promise<CancelOrderResponse> {
		const value = orderId?.trim()
		if (!value) {
			throw new Error("orderId is required")
		}

		return this.request(
			"POST",
			`/orders/${encodeURIComponent(value)}/cancel`,
			params || {},
		)
	}

	/**
	 * Refund a paid order owned by this app.
	 *
	 * Orders carrying subscriptions or entitlements require a full refund and
	 * entitlement revocation. Reuse the same idempotency key when retrying.
	 */
	async refundOrder(
		orderId: string,
		params: RefundOrderParams,
	): Promise<RefundOrderResponse> {
		const value = orderId?.trim()
		if (!value) throw new Error("orderId is required")
		if (!/^[A-Za-z0-9._:-]{8,120}$/.test(params.idempotencyKey?.trim())) {
			throw new Error("A valid idempotencyKey is required")
		}
		if (
			params.amount !== undefined &&
			(!Number.isInteger(params.amount) || params.amount <= 0)
		) {
			throw new Error("amount must be a positive integer")
		}

		return this.request("POST", `/orders/${encodeURIComponent(value)}/refund`, {
			...params,
			idempotencyKey: params.idempotencyKey.trim(),
		})
	}

	/**
	 * Complete a zero-amount FREE order.
	 * This marks the pending order as paid and triggers normal paid-order side effects.
	 */
	async completeFreeOrder(orderId: string): Promise<CompleteFreeOrderResponse> {
		const value = orderId?.trim()
		if (!value) {
			throw new Error("orderId is required")
		}

		return this.request(
			"POST",
			`/orders/${encodeURIComponent(value)}/free/complete`,
			{},
		)
	}

	/**
	 * Query order status
	 * @param orderId - The order ID to query
	 */
	async getOrderStatus(orderId: string): Promise<OrderStatus> {
		return this.request("GET", `/orders/${orderId}`)
	}

	/**
	 * Get order details (full order information)
	 * @param orderId - The order ID to query
	 */
	async getOrderDetails(orderId: string): Promise<OrderDetails> {
		return this.request("GET", `/orders/${orderId}/details`)
	}

	/**
	 * Get orders list with pagination
	 * @param params - Query parameters (pagination, filters)
	 * @returns Orders list and pagination info
	 */
	async getOrders(params?: GetOrdersParams): Promise<GetOrdersResponse> {
		const queryParams = new URLSearchParams()
		if (params?.page) queryParams.append("page", params.page.toString())
		if (params?.pageSize)
			queryParams.append("pageSize", params.pageSize.toString())
		if (params?.userId) queryParams.append("userId", params.userId)
		if (params?.status) queryParams.append("status", params.status)
		if (params?.startDate) queryParams.append("startDate", params.startDate)
		if (params?.endDate) queryParams.append("endDate", params.endDate)

		const path = queryParams.toString()
			? `/orders?${queryParams.toString()}`
			: "/orders"
		return this.request<GetOrdersResponse>("GET", path)
	}

	/**
	 * Get user entitlements in the legacy flat shape.
	 * @param userId - User ID
	 */
	async getEntitlements(userId: string): Promise<Record<string, any>> {
		return this.request("GET", `/users/${userId}/entitlements`)
	}

	/**
	 * Get user entitlements with full details (type, expiry, reset config, source)
	 * @param userId - User ID
	 */
	async getEntitlementsDetail(userId: string): Promise<EntitlementDetail> {
		return this.request("GET", `/users/${userId}/entitlements/detail`)
	}

	async getActiveSubscription(
		userId: string,
	): Promise<ActiveSubscriptionInfo | null> {
		return this.request("GET", `/users/${userId}/active-subscription`)
	}

	async getSubscriptionUpgradeOptions(
		userId: string,
		options?: {
			locale?: string
			currency?: string
			subscriptionIntent?: Extract<
				SubscriptionIntent,
				"UPGRADE" | "QUEUED_UPGRADE"
			>
		},
	): Promise<Product[]> {
		const value = userId?.trim()
		if (!value) throw new Error("userId is required")
		const params = new URLSearchParams()
		if (options?.locale) params.set("locale", options.locale)
		if (options?.currency) params.set("currency", options.currency)
		if (options?.subscriptionIntent) {
			params.set("subscriptionIntent", options.subscriptionIntent)
		}
		const query = params.toString() ? `?${params.toString()}` : ""
		return this.request(
			"GET",
			`/users/${encodeURIComponent(value)}/subscription-upgrade-options${query}`,
		)
	}

	/**
	 * Ensure user exists and auto-assign trial product if new user
	 * This should be called when user first logs in or registers
	 * @param userId - User ID
	 */
	async ensureUserWithTrial(
		userId: string,
	): Promise<EnsureUserWithTrialResponse> {
		return this.request("POST", `/users/${userId}/entitlements/bootstrap`, {})
	}

	/**
	 * Get a single entitlement value
	 * @param userId - User ID
	 * @param key - Entitlement key
	 */
	async getEntitlementValue(userId: string, key: string): Promise<any> {
		const entitlements = await this.getEntitlements(userId)
		return entitlements[key] ?? null
	}

	/**
	 * Consume numeric entitlement
	 * @param userId - User ID
	 * @param key - Entitlement key
	 * @param amount - Amount to consume
	 */
	async consumeEntitlement(
		userId: string,
		key: string,
		amount: number,
		options?: {
			idempotencyKey?: string
			metadata?: Record<string, any>
		},
	): Promise<{ balance: number }> {
		return this.request("POST", `/users/${userId}/entitlements/consume`, {
			key,
			amount,
			...options,
		})
	}

	/**
	 * Consume numeric entitlements from an ordered key pool.
	 * Useful when subscription credits and perpetual credits use separate keys.
	 */
	async consumeEntitlementPool(
		userId: string,
		keys: string[],
		amount: number,
		options?: {
			idempotencyKey?: string
			metadata?: Record<string, any>
		},
	): Promise<ConsumeEntitlementPoolResult> {
		return this.request("POST", `/users/${userId}/entitlements/consume-pool`, {
			keys,
			amount,
			...options,
		})
	}

	/**
	 * Get active numeric entitlement buckets ordered by expiration.
	 */
	async getEntitlementCreditBuckets(
		userId: string,
		keys?: string[],
	): Promise<EntitlementCreditBucket[]> {
		const query =
			keys && keys.length > 0
				? `?keys=${encodeURIComponent(keys.join(","))}`
				: ""
		return this.request(
			"GET",
			`/users/${userId}/entitlements/credit-buckets${query}`,
		)
	}

	/**
	 * Add numeric entitlement (e.g. refund)
	 * @param userId - User ID
	 * @param key - Entitlement key
	 * @param amount - Amount to add
	 */
	async addEntitlement(
		userId: string,
		key: string,
		amount: number,
	): Promise<{ balance: number }> {
		return this.request("POST", `/users/${userId}/entitlements/add`, {
			key,
			amount,
		})
	}

	/**
	 * Toggle boolean entitlement
	 * @param userId - User ID
	 * @param key - Entitlement key
	 * @param enabled - Whether to enable
	 */
	async toggleEntitlement(
		userId: string,
		key: string,
		enabled: boolean,
	): Promise<{ isEnabled: boolean }> {
		// Toggle endpoint expects POST with enabled flag
		// However, looking at list_dir, we have toggle/route.ts
		// I should verify its contract, but assuming standard toggle pattern:
		return this.request("POST", `/users/${userId}/entitlements/toggle`, {
			key,
			enabled,
		})
	}

	/**
	 * Generate checkout URL for client-side payment
	 * @param productId - Product ID
	 * @param priceId - Price ID
	 * @returns Checkout page URL
	 */
	getCheckoutUrl(productId: string, priceId: string): string {
		return `${this.checkoutUrl}/checkout/${this.appId}/${productId}/${priceId}`
	}

	/**
	 * Verify a hosted login token and return the normalized login profile.
	 * This request is signed with your app credentials and routed through the worker API.
	 */
	async verifyLoginToken(token: string): Promise<VerifiedLoginToken> {
		if (!token?.trim()) {
			throw new Error("login token is required")
		}
		return this.request("POST", "/login/tokens/verify", { token: token.trim() })
	}

	/**
	 * Send a phone verification code for binding a phone number to a hosted login user.
	 */
	async sendPhoneVerificationCode(
		params: SendPhoneVerificationCodeParams,
	): Promise<SendPhoneVerificationCodeResponse> {
		const userId = params.userId?.trim()
		const phoneNumber = params.phoneNumber?.trim()
		if (!userId) throw new Error("userId is required")
		if (!phoneNumber) throw new Error("phoneNumber is required")

		return this.request(
			"POST",
			`/login/users/${encodeURIComponent(userId)}/phone/code`,
			{
				phoneCountryCode: params.phoneCountryCode || params.countryCode,
				phoneNumber,
			},
		)
	}

	/**
	 * Query the verified phone binding for a hosted login user.
	 */
	async getPhoneBinding(params: GetPhoneBindingParams): Promise<PhoneBinding> {
		const userId = params.userId?.trim()
		if (!userId) throw new Error("userId is required")

		return this.request(
			"GET",
			`/login/users/${encodeURIComponent(userId)}/phone`,
		)
	}

	/**
	 * Bind a verified phone number to a hosted login user, merging existing accounts when needed.
	 */
	async bindPhoneNumber(
		params: BindPhoneNumberParams,
	): Promise<BindPhoneNumberResponse> {
		const userId = params.userId?.trim()
		const phoneNumber = params.phoneNumber?.trim()
		const code = params.code?.trim()
		if (!userId) throw new Error("userId is required")
		if (!phoneNumber) throw new Error("phoneNumber is required")
		if (!code) throw new Error("code is required")

		return this.request(
			"POST",
			`/login/users/${encodeURIComponent(userId)}/phone/bind`,
			{
				code,
				phoneCountryCode: params.phoneCountryCode || params.countryCode,
				phoneNumber,
			},
		)
	}

	/**
	 * Update a hosted login user's phone number after verifying the new number by SMS code.
	 */
	async updatePhoneNumber(
		params: UpdatePhoneNumberParams,
	): Promise<UpdatePhoneNumberResponse> {
		const userId = params.userId?.trim()
		const phoneNumber = params.phoneNumber?.trim()
		const code = params.code?.trim()
		if (!userId) throw new Error("userId is required")
		if (!phoneNumber) throw new Error("phoneNumber is required")
		if (!code) throw new Error("code is required")

		return this.request(
			"PUT",
			`/login/users/${encodeURIComponent(userId)}/phone`,
			{
				code,
				phoneCountryCode: params.phoneCountryCode || params.countryCode,
				phoneNumber,
			},
		)
	}

	/**
	 * Create a hosted WeChat official account binding URL for a user.
	 */
	async createWechatMessageBinding(
		params: CreateWechatMessageBindingParams,
	): Promise<CreateWechatMessageBindingResponse> {
		const loginExternalUserId =
			params.loginExternalUserId?.trim() || params.userId?.trim()
		const merchantUserId = params.merchantUserId?.trim()
		if (!loginExternalUserId && !merchantUserId) {
			throw new Error("userId or merchantUserId is required")
		}

		return this.request("POST", "/messages/wechat/bindings", {
			...params,
			loginExternalUserId,
			merchantUserId,
		})
	}

	/**
	 * Send an app-linked message template to a bound recipient.
	 */
	async sendMessage(params: SendMessageParams): Promise<SendMessageResponse> {
		if (!params.templateId?.trim() && !params.templateCode?.trim()) {
			throw new Error("templateId or templateCode is required")
		}
		if (!params.data || typeof params.data !== "object") {
			throw new Error("data is required")
		}

		return this.request("POST", "/messages/send", params)
	}
}
